<template lang="pug">
.resource-metadata-editor.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/home"
        )
          | Back to home page
    .ms-auto
    ToggleDark

  .flex-1.p-12.overflow-y-auto
    .max-w-screen-xl.mx-auto
      .prose.dark_prose-invert.max-w-none
        h1.font-normal
          | Manage Portal Library Metadata for Resource {{ resourceId }}
        p.opacity-80.italic.text-xl
          | Product Type: {{ productTypeLabel }}

        //- Loading and Error States
        .alert.alert-error.bg-red-100.dark_bg-red-900.border.border-red-400.dark_border-red-700.text-red-700.dark_text-red-400.px-4.py-3.rounded.mb-4(v-if="error")
          | {{ error }}
        
        .alert.alert-info.bg-blue-100.dark_bg-blue-900.border.border-blue-400.dark_border-blue-700.text-blue-700.dark_text-blue-400.px-4.py-3.rounded.mb-4(v-if="loading")
          | Loading resource data...

      //- Status Panel
      .panel.prose.dark_prose-invert.max-w-screen-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.my-4.sticky.top-0.z-20.shadow-lg.border.border-gray-200.dark_border-gray-600.screen-xl(v-if="!loading && !isCompact")
        .flex.justify-between.items-start
          div.flex-1.pl-8
            h2.text-xl.font-bold.mb-4 Status
            ul.status-list.list-none.p-0.m-0
              li.flex.justify-between.items-center.mb-2.gap-4.p-2.rounded(v-for="(section, sectionName) in orderedSections" :key="sectionName" :class="{ 'bg-gray-50 dark_bg-gray-700': section.order % 2 === 1 }")
                span.text-gray-900.dark_text-gray-100.font-bold.text-lg {{ section.title }}
                span.font-bold.text-lg(:class="getStatusDisplay(sectionStatus[sectionName]).color") {{ getStatusDisplay(sectionStatus[sectionName]).text }}
            
            .text-right.mt-4
              button.btn.bg-gray-500.text-white.py-2.px-4.rounded.mr-2(
                v-if="showGlobalResetButton"
                @click="openResetModal('all')"
              ) Reset Form
              button.save-btn.bg-primary-600.text-white.py-2.px-4.rounded(
                @click="handlePublishUpdate"
                :disabled="!canPublish"
                :class="canPublish ? 'bg-primary-600' : 'bg-gray-400 opacity-50 cursor-not-allowed'"
              ) {{ publishedData && Object.keys(publishedData).length > 0 ? 'Update' : 'Publish' }}
          button.btn.btn-icon.ripple.ripple--centered.text-gray-800.dark_text-gray-200.p-2.text-xl.h-10.items-center.ml-4(
            @click="toggleCompact"
            aria-label="Toggle compact view"
            type="button"
          )
            Icon(icon="teenyicons:down-small-outline")

      //- Compact Status Panel
      .panel.prose.dark_prose-invert.max-w-screen-xl.mx-auto.bg-gray-100.dark_bg-gray-800.p-4.rounded-lg.my-4.sticky.top-0.z-20.shadow-lg.border.border-gray-200.dark_border-gray-600(v-if="!loading && isCompact")
        .flex.justify-between.items-center.h-12
          .flex.space-x-6.items-center
            .flex.items-center.space-x-2(v-for="(section, sectionName) in orderedSections" :key="sectionName")
              span.text-base.font-bold.text-gray-900.dark_text-gray-100 {{ section.order }}
              span.text-sm.font-bold.px-3.py-2.rounded(:class="getStatusBadgeClass(sectionStatus[sectionName])") {{ getStatusDisplay(sectionStatus[sectionName]).text }}
          .flex.items-center.space-x-4
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded.mr-2(
              v-if="showGlobalResetButton"
              @click="openResetModal('all')"
            ) Reset
            button.save-btn.bg-primary-600.text-white.py-2.px-4.rounded(
              @click="handlePublishUpdate"
              :disabled="!canPublish"
              :class="canPublish ? 'bg-primary-600' : 'bg-gray-400 opacity-50 cursor-not-allowed'"
            ) {{ publishedData && Object.keys(publishedData).length > 0 ? 'Update' : 'Publish' }}
            button.btn.btn-icon.ripple.ripple--centered.text-gray-800.dark_text-gray-200.p-2.text-xl.h-10.items-center(
              @click="toggleCompact"
              aria-label="Toggle compact view"
              type="button"
            )
              Icon(icon="teenyicons:up-small-outline")

      //- Reset Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showResetModal" @click="closeResetModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4 {{ resetModalTitle }}
          p.text-gray-700.dark_text-gray-300.mb-6
            | Resetting will restore the data to the last published version and cannot be undone. 
            | Any changes will be lost.
          .flex.justify-end.space-x-3
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closeResetModal"
            ) Cancel
            button.reset-confirm-btn.bg-red-600.text-white.py-2.px-4.rounded(
              @click="confirmReset"
            ) Reset

      //- Publish Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showPublishModal" @click="closePublishModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4(v-if="isUpdateMode") Update {{ formData.title || 'Resource' }} on the Portal Library?
          h3.text-xl.font-bold.mb-4(v-else) Publish {{ formData.title || 'Resource' }} to the Portal Library?
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode")
            | These updates will take effect IMMEDIATELY in the Vinci Portal Library. Please double check that you have not changed fields with significant meaning like suite, topic, industry, retired, or exclude from demo.
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode") If you are unsure, hit cancel and reset form.
          p.text-gray-700.dark_text-gray-300.mb-6(v-if="isUpdateMode") If you are sure, please hit Update.
          div(v-else)
            p.text-gray-700.dark_text-gray-300.mb-6
              | This is a first-time publish which will add this resource LIVE to the Vinci Portal Library. This will then be visible to all clients.
            p.text-gray-700.dark_text-gray-300.mb-6 Are you sure you want to continue?
          .flex.justify-end.space-x-3
            button.btn.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closePublishModal"
              :disabled="isPublishing"
            ) Cancel
            button.reset-confirm-btn.bg-red-600.text-white.py-2.px-4.rounded.flex.items-center(
              @click="confirmPublish"
              :disabled="isPublishing"
            )
              span.mr-2(v-if="isPublishing")
                Icon(icon="mdi:loading" class="animate-spin")
              span {{ isPublishing ? (isUpdateMode ? 'Updating...' : 'Publishing...') : (isUpdateMode ? 'Update' : 'Publish') }}

      //- Section 1: Title & Category
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionTitle.order}. ${SECTION_CONFIG.sectionTitle.title}`"
        sectionName="sectionTitle"
        :isSaving="savingSections.sectionTitle"
        :canSave="canSaveSection('sectionTitle')"
        :canReset="canResetSection('sectionTitle')"
        @save="saveSection('sectionTitle')"
        @reset="openResetModal('sectionTitle')"
      )
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2(for="title")
              | Title 
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="title"
              v-model="formData.title"
              placeholder="Enter resource title"
            )
          div
            //- Empty div for layout balance
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2(for="suiteTitle")
              | Suite 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="suiteTitle"
              v-model="formData.suiteTitle"
            )
              option(value="" disabled) Select suite
              option(v-for="suite in suites" :key="suite.id" :value="suite.title") {{ suite.title }}
          div
            label.block.mb-2(for="topicTitle")
              | Topic 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="topicTitle"
              v-model="formData.topicTitle"
            )
              option(value="" disabled) Select topic
              option(v-for="topic in filteredTopics" :key="topic.id" :value="topic.title") {{ topic.title }}

      //- Section 2: Type
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionType.order}. ${SECTION_CONFIG.sectionType.title}`"
        sectionName="sectionType"
        :isSaving="savingSections.sectionType"
        :canSave="canSaveSection('sectionType')"
        :canReset="canResetSection('sectionType')"
        @save="saveSection('sectionType')"
        @reset="openResetModal('sectionType')"
      )
        //- Resource Type and Content Type
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2 Resource Type
            .inline-block.px-3.py-1.bg-primary-100.dark_bg-primary-900.text-primary-800.dark_text-primary-200.rounded.font-semibold
              | {{ productTypeLabel }}
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2
              | Resource type is set when the resource is created and cannot be changed.
          div(v-if="isFieldShown('contentType')")
            label.block.mb-2(for="contentType")
              | Content Type
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="contentType"
              v-model="formData.contentType"
            )
              option(value="") Select content type
              option(value="doc") doc
              option(value="docx") docx
              option(value="dotx") dotx
              option(value="HLS") HLS
              option(value="HTML") HTML
              option(value="pdf") pdf
              option(value="xlsx") xlsx
              option(value="N/A") N/A

        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-2(for="activityType")
              | Activity Type 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="activityType"
              v-model="formData.activityType"
            )
              option(value="" disabled) Select activity type
              option(v-for="activityType in activityTypes" :key="activityType.activityTypeId" :value="activityType.name") {{ activityType.name }}
          div
            label.block.mb-2(for="level")
              | Level 
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="level"
              v-model="formData.level"
            )
              option(value="" disabled) Select level
              option(v-for="level in levelOptions" :key="level" :value="level") {{ level }}
        
        .grid.grid-cols-2.gap-6.mb-6(v-if="showDuration")
          div
            label.block.mb-2(for="duration")
              | Duration (HH:MM:SS) 
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="duration"
              v-model="formData.duration"
              placeholder="00:30:00"
              pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
              title="Please enter the duration in HH:MM:SS format."
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | Enter duration in hours:minutes:seconds format (e.g., 00:30:00 for 30 minutes).
          div
            //- Empty div for layout balance

      //- Section 3: Description & Objectives
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionDescriptions.order}. ${SECTION_CONFIG.sectionDescriptions.title}`"
        sectionName="sectionDescriptions"
        :isSaving="savingSections.sectionDescriptions"
        :canSave="canSaveSection('sectionDescriptions')"
        :canReset="canResetSection('sectionDescriptions')"
        @save="saveSection('sectionDescriptions')"
        @reset="openResetModal('sectionDescriptions')"
      )
        label.block.mb-0(for="descriptionHTML")
          | Description
          span.text-red-500.required-asterisk *
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Please write a description about the resource.
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | You can write multiple paragraphs by leaving a blank line between them. Plain text will be automatically converted to HTML.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
          id="descriptionHTML"
          v-model="descriptionHTMLText"
          rows="4"
          placeholder="Write a resource description."
        )
        label.block.mb-0(for="objectivesHTML")
          | Objectives
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | What are the learning objectives of the resource?
        p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
          | Each objective should be on its own line preceded by a dash. Plain text will be automatically converted to HTML.
        textarea.w-full.p-2.mb-4.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
          id="objectivesHTML"
          v-model="objectivesHTMLText"
          rows="4"
          placeholder="- Learning Objective 1"
        )

      //- Section 4: Audience
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionAudience.order}. ${SECTION_CONFIG.sectionAudience.title}`"
        sectionName="sectionAudience"
        :isSaving="savingSections.sectionAudience"
        :canSave="canSaveSection('sectionAudience')"
        :canReset="canResetSection('sectionAudience')"
        @save="saveSection('sectionAudience')"
        @reset="openResetModal('sectionAudience')"
      )
        .grid.grid-cols-2.gap-6.mb-6
          div
            label.block.mb-0(for="industry")
              | Industry 
              span.text-red-500.required-asterisk *
            HierarchicalIndustrySelect(
              v-model="formData.additionalInfo.industryId"
              :industries="industries"
              placeholder="Select industry"
              instructionalText="Please select the lowest level industry that applies to this resource."
              @change="handleIndustryChange"
            )
        div.mb-6
          HierarchicalMultiSelectDropdown(
            v-model="formData.additionalInfo.Jurisdiction"
            @update:jurisdictionIds="formData.additionalInfo.jurisdictionId = $event"
            :jurisdictions="jurisdictions"
            :dataType="'array'"
            label="Jurisdiction"
            :required="true"
            placeholder="Select jurisdictions"
            instructionalText="Please select all of the applicable jurisdictions."
          )
        div.mb-6
          MultiSelectDropdown(
            v-model="formData.targetLearners"
            :options="targetLearners"
            :dataType="'array'"
            label="Target Learners"
            :required="true"
            placeholder="Select target learners"
          )

      //- Section 5: Additional Info
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionInfo.order}. ${SECTION_CONFIG.sectionInfo.title}`"
        sectionName="sectionInfo"
        :isSaving="savingSections.sectionInfo"
        :canSave="canSaveSection('sectionInfo')"
        :canReset="canResetSection('sectionInfo')"
        @save="saveSection('sectionInfo')"
        @reset="openResetModal('sectionInfo')"
      )
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-2(for="retired")
              | Retired
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="retired"
              v-model="formData.retired"
            )
              option(:value="false") No
              option(:value="true") Yes
          div
            label.block.mb-2(for="flagship")
              | Flagship
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="flagship"
              v-model="formData.flagship"
            )
              option(value="false") No
              option(value="true") Yes
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-2(for="triggerWarning")
              | Trigger Warning
              span.text-red-500.required-asterisk *
            select.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="triggerWarning"
              v-model="formData.additionalInfo.triggerWarning"
            )
              option(:value="false") No
              option(:value="true") Yes
          div
            //- Empty div for layout balance
        
        //- Related To Selection
        .mt-6
          CourseIdSelect(
            v-model="formData.additionalInfo.relatedTo"
            :courses="transformedLibraryCourses"
            label="Related To"
            instructionalText="Select courses with similar objectives/duration/audience etc."
            placeholder="Search for courses..."
            :disabled="loadingLibraryCourses"
            :multiple="true"
            valueType="array"
          )

        //- Legislation Field
        .grid.grid-cols-2.gap-6.mt-6
          div
            label.block.mb-0(for="legislation")
              | Legislation
            p.text-sm.text-gray-500.dark_text-gray-400.mb-2.mt-0
              | Each piece of legislation should be on its own line preceded by a dash.
            textarea.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              id="legislation"
              v-model="legislationText"
              rows="3"
              placeholder="- Sample Legislation 1"
            )

      //- Section 6: Assets
      FormSection(
        v-if="!loading"
        :title="`${SECTION_CONFIG.sectionAssets.order}. ${SECTION_CONFIG.sectionAssets.title}`"
        sectionName="sectionAssets"
        :isSaving="savingSections.sectionAssets"
        :canSave="canSaveSection('sectionAssets')"
        :canReset="canResetSection('sectionAssets')"
        @save="saveSection('sectionAssets')"
        @reset="openResetModal('sectionAssets')"
      )
        .mt-6.space-y-6
          div(v-if="isFieldShown('resourceUrl')")
            label.block.mb-2(for="resourceUrl")
              | Resource URL
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="resourceUrl"
              v-model="formData.resourceUrl"
              placeholder="Enter resource URL"
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | URL to the file for this resource.
          div
            label.block.mb-2(for="thumbnailURL")
              | Thumbnail URL
              span.text-red-500.required-asterisk *
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="thumbnailURL"
              v-model="formData.thumbnailURL"
              placeholder="https://portal.vinciworks.com/client-assets/2000000.jpg"
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | Default thumbnail URL format: https://portal.vinciworks.com/client-assets/{{ resourceId }}.jpg
          div(v-if="productType === 'video'")
            label.block.mb-2(for="videoTranscriptURL")
              | Video Transcript URL
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="videoTranscriptURL"
              v-model="formData.videoTranscriptURL"
              placeholder="Enter PDF transcript URL"
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | URL to the PDF transcript file for this video.
          div(v-if="productType === 'video'")
            label.block.mb-2(for="videoCaptionUrl")
              | Video Caption URL
            input.w-full.p-2.bg-white.dark_bg-gray-700.border.border-gray-300.dark_border-gray-600.rounded(
              type="text"
              id="videoCaptionUrl"
              v-model="formData.videoCaptionUrl"
              placeholder="Enter VTT caption file URL"
            )
            p.text-sm.text-gray-500.dark_text-gray-400.mt-2.mb-0
              | URL to the VTT caption file for this video.
          div(v-if="productType === 'playlist'").mt-6
            OrderedResourceSelect(
              v-model="formData.hasResourceIds"
              :resources="transformedLibraryResources"
              label="Playlist resources"
              instructionalText="Add video and webinar resources in the order they should appear. Drag to reorder."
              placeholder="Search for resources..."
              :disabled="loadingLibraryResources"
            )

      //- Delete button at bottom
      .max-w-screen-xl.mx-auto.mt-8.mb-16(v-if="false")
        .text-center(v-if="publishedData && Object.keys(publishedData).length > 0")
          button.reset-confirm-btn.bg-red-600.text-white.py-2.px-6.rounded(
            @click="openDeleteModal"
          ) Delete

      //- Delete Confirmation Modal
      .modal-overlay.fixed.inset-0.bg-black.bg-opacity-50.z-50.flex.items-center.justify-center(v-if="showDeleteModal" @click="closeDeleteModal")
        .modal-content.prose.dark_prose-invert.max-w-md.mx-auto.bg-gray-100.dark_bg-gray-800.p-6.rounded-lg.shadow-lg.border.border-gray-200.dark_border-gray-600(@click.stop)
          h3.text-xl.font-bold.mb-4 Delete {{ formData.title || 'Resource' }} from Portal Library?
          p.text-gray-700.dark_text-gray-300.mb-6
            | This process will remove this resource from the Portal Library. This should only be done during extreme or urgent situations.
          p.text-gray-700.dark_text-gray-300.mb-6 Are you sure you want to continue?
          .flex.flex-wrap.justify-end.gap-3.mt-4.px-2
            button.reset-btn-red.bg-gray-500.text-white.py-2.px-4.rounded(
              @click="closeDeleteModal"
              :disabled="isDeleting"
            ) Cancel
            button.reset-confirm-btn.bg-red-700.text-white.py-2.px-4.rounded.flex.items-center(
              @click="confirmDelete"
              :disabled="isDeleting"
            )
              span.mr-2(v-if="isDeleting")
                Icon(icon="mdi:loading" class="animate-spin")
              span {{ isDeleting ? 'Deleting...' : 'Delete' }}
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, reactive } from 'vue';
import { Icon } from '@iconify/vue';
import MainMenu from './MainMenu.vue';
import ToggleDark from './ToggleDark.vue';
import FormSection from './FormSection.vue';
import HierarchicalIndustrySelect from './HierarchicalIndustrySelect.vue';
import HierarchicalMultiSelectDropdown from './HierarchicalMultiSelectDropdown.vue';
import MultiSelectDropdown from './MultiSelectDropdown.vue';
import CourseIdSelect from './CourseIdSelect.vue';
import OrderedResourceSelect from './OrderedResourceSelect.vue';
import {
  getResourceFromLibrary,
  getResourceDraftMetadata,
  updateResourceDraftMetadataSection,
  getSuitesAndTopics,
  deleteResourceFromLibrary,
  getIndustries,
  getJurisdictions,
  getTargetLearners,
  getAllCoursesFromLibrary,
  publishResourceToLibrary,
  updateResourceInLibrary
} from '../lib/callModContentAuthors';
import { SECTION_CONFIG, FIELD_CONFIG } from '../lib/resourceMetadataConfig';
import {
  getActivityTypesForResourceType,
  getResourceTypeId,
  getActivityTypeId,
  getResourceTypeNameById,
  type ResourceTypeName
} from '../lib/resourceTypesUtil';
import {
  getNestedValue,
  setNestedValue,
  calculateResourceSectionStatus,
  areValuesEqual,
  compareObjects,
  convertHtmlToText,
  convertTextToHtml,
  stripHtml
} from '../lib/metadataUtils';

const props = defineProps<{
  resourceId: number;
  firmCode?: string;
  productType?:
    | 'video'
    | 'webinar'
    | 'guide'
    | 'template'
    | 'blog'
    | 'playlist';
}>();

// Derived productType from resource data if not provided in props
const derivedProductType = ref<
  'video' | 'webinar' | 'guide' | 'template' | 'blog' | 'playlist' | undefined
>(undefined);

// Computed productType that uses props if provided, otherwise derived from resource data
const productType = computed(() => {
  return props.productType || derivedProductType.value;
});

const productTypeLabel = computed(() => {
  if (!productType.value) return 'Unknown';
  const labels = {
    video: 'Video',
    webinar: 'Webinar',
    guide: 'Guide',
    template: 'Template',
    blog: 'Blog',
    playlist: 'Playlist'
  };
  return labels[productType.value] || productType.value;
});

// State
const loading = ref(true);
const error = ref('');

// Dropdown data
const suites = ref<any[]>([]);
const topics = ref<any[]>([]);
const industries = ref<any>({});
const jurisdictions = ref<any>({});
const targetLearners = ref<string[]>([]);

// All library courses for relationship selection
const allLibraryCourses = ref<any[]>([]);
const loadingLibraryCourses = ref(false);

// All library resources (type=resource) for playlist hasResourceIds selector
const allLibraryResources = ref<any[]>([]);
const loadingLibraryResources = ref(false);

// Published data from mod-library-v2
const publishedData = ref<any>(null);

// Draft data from mod-content (entire JSONB)
const draftData = ref<any>(null);

// Form data (reactive object that will hold all form values)
const formData = ref<any>({});

// Section status tracking
const SECTION_STATUSES = {
  NOT_STARTED: 'NOT_STARTED',
  READY_TO_PUBLISH: 'READY_TO_PUBLISH',
  READY_TO_UPDATE: 'READY_TO_UPDATE',
  PUBLISHED: 'PUBLISHED',
  UNSAVED_CHANGES: 'UNSAVED_CHANGES'
};

// Initialize section status for all sections
const sectionStatus = reactive<Record<string, string>>({});
Object.keys(SECTION_CONFIG).forEach(sectionName => {
  sectionStatus[sectionName] = SECTION_STATUSES.NOT_STARTED;
});

// Get sections in order for dynamic rendering
const orderedSections = computed(() => {
  return Object.fromEntries(
    Object.entries(SECTION_CONFIG).sort(([, a], [, b]) => a.order - b.order)
  );
});

// Compact view toggle
const isCompact = ref(true);
const toggleCompact = () => {
  isCompact.value = !isCompact.value;
};

// Publish modal state
const showPublishModal = ref(false);
const isPublishing = ref(false);
const isUpdateMode = computed(
  () => !!(publishedData.value && Object.keys(publishedData.value).length > 0)
);

// Delete modal state
const showDeleteModal = ref(false);
const isDeleting = ref(false);

// Reset modal state
const showResetModal = ref(false);
const resetModalTitle = ref('');
const resetTarget = ref<string>(''); // 'all' or section name

// Loading states for each section
const savingSections = reactive<Record<string, boolean>>({});
Object.keys(SECTION_CONFIG).forEach(sectionName => {
  savingSections[sectionName] = false;
});

// Filter topics based on selected suite
const filteredTopics = computed(() => {
  if (!formData.value.suiteTitle) return [];

  const selectedSuite = suites.value.find(
    suite => suite.title === formData.value.suiteTitle
  );
  if (!selectedSuite) return [];

  return topics.value.filter(
    topic => topic.internalParentId === selectedSuite.internalId
  );
});

// Get activity types for current resource type
const activityTypes = computed(() => {
  const resourceTypeName = productTypeLabel.value as ResourceTypeName;
  return getActivityTypesForResourceType(resourceTypeName);
});

// Level options
const levelOptions = ['All Levels', 'Intermediate', 'Advanced'];

// Check if duration field should be shown (only for video/webinar)
const showDuration = computed(() => {
  return productType.value === 'video' || productType.value === 'webinar';
});

// Computed properties for HTML-to-text conversion
const descriptionHTMLText = computed({
  get: () => {
    // Use descriptionHTML as source of truth for comparison
    const html = getNestedValue(
      formData.value,
      'additionalInfo.descriptionHTML'
    );
    return convertHtmlToText(html || '', 'paragraph');
  },
  set: (value: string) => {
    const html = convertTextToHtml(value, 'paragraph');
    // Set descriptionHTML as source of truth
    setNestedValue(formData.value, 'additionalInfo.descriptionHTML', html);
    // Also set plain text description for backwards compatibility (derived from HTML)
    const plainText = stripHtml(html);
    setNestedValue(formData.value, 'additionalInfo.description', plainText);
  }
});

const objectivesHTMLText = computed({
  get: () => {
    const html = getNestedValue(formData.value, 'objectivesHTML');
    return convertHtmlToText(html || '', 'list');
  },
  set: (value: string) => {
    const html = convertTextToHtml(value, 'list');
    setNestedValue(formData.value, 'objectivesHTML', html);
  }
});

// Computed property for legislation HTML-to-text conversion
const legislationText = computed({
  get: () => {
    const html = getNestedValue(formData.value, 'additionalInfo.legislation');
    return convertHtmlToText(html || '', 'list');
  },
  set: (value: string) => {
    const html = convertTextToHtml(value, 'list');
    // Save the HTML value - convertTextToHtml returns '' for empty input, null for null/undefined
    // For optional fields, we want to save null when empty, not empty string
    const finalValue = html && html.trim() ? html : null;
    setNestedValue(formData.value, 'additionalInfo.legislation', finalValue);
  }
});

// Fetch suites and topics from mod-content
const fetchDropdownData = async () => {
  try {
    const [suitesData, industriesData, jurisdictionsData, targetLearnersData] =
      await Promise.all([
        getSuitesAndTopics(),
        getIndustries(),
        getJurisdictions(),
        getTargetLearners()
      ]);

    // Separate suites (internalParentId: 0) from topics (internalParentId > 0)
    const allItems = suitesData as any[];
    suites.value = allItems.filter((item: any) => item.internalParentId === 0);
    topics.value = allItems.filter((item: any) => item.internalParentId > 0);

    // Set industries, jurisdictions, and target learners
    industries.value = (industriesData as any).data || {};
    jurisdictions.value = (jurisdictionsData as any).data || {};
    targetLearners.value = (targetLearnersData as any).data || [];

    console.log('[ResourceMetadataEditor] Loaded suites:', suites.value.length);
    console.log('[ResourceMetadataEditor] Loaded topics:', topics.value.length);
    console.log(
      '[ResourceMetadataEditor] Loaded industries:',
      Object.keys(industries.value).length
    );
    console.log(
      '[ResourceMetadataEditor] Loaded jurisdictions:',
      Object.keys(jurisdictions.value).length
    );
    console.log(
      '[ResourceMetadataEditor] Loaded target learners:',
      targetLearners.value.length
    );
  } catch (err: any) {
    console.error(
      '[ResourceMetadataEditor] Error fetching dropdown data:',
      err
    );
    error.value = `Failed to load dropdown data: ${err.message}`;
  }
};

// Fetch all courses from Portal Library (for CourseIdSelect)
const fetchAllLibraryCourses = async () => {
  try {
    loadingLibraryCourses.value = true;
    const response = await getAllCoursesFromLibrary();
    console.log('[ResourceMetadataEditor] Raw API response:', response);
    console.log('[ResourceMetadataEditor] Response data:', response.data);
    console.log(
      '[ResourceMetadataEditor] Response data.results:',
      response.data?.results
    );

    // Extract courses from the correct response structure
    allLibraryCourses.value = (response as any).data?.data?.results || [];
    console.log(
      '[ResourceMetadataEditor] All library courses loaded:',
      allLibraryCourses.value.length,
      'courses'
    );
    console.log(
      '[ResourceMetadataEditor] First course sample:',
      allLibraryCourses.value[0]
    );
  } catch (err: any) {
    console.error(
      '[ResourceMetadataEditor] Error fetching all library courses:',
      err
    );
    error.value = `Failed to load library courses: ${err.message}`;
  } finally {
    loadingLibraryCourses.value = false;
  }
};

// Transform library courses for component use
const transformedLibraryCourses = computed(() => {
  console.log(
    '[ResourceMetadataEditor] Transforming courses:',
    allLibraryCourses.value.length,
    'courses'
  );
  const transformed = allLibraryCourses.value
    .map(course => ({
      id: course.id,
      courseId: course.vinciResourceId,
      title: course.title
    }))
    .sort((a, b) => a.courseId - b.courseId); // Sort by courseId (vinciResourceId)
  console.log(
    '[ResourceMetadataEditor] Transformed courses:',
    transformed.length,
    'courses'
  );
  console.log(
    '[ResourceMetadataEditor] First transformed course:',
    transformed[0]
  );
  return transformed;
});

// Fetch all resources from Portal Library (for OrderedResourceSelect on playlists)
const fetchAllLibraryResources = async () => {
  try {
    loadingLibraryResources.value = true;
    const response = await getAllCoursesFromLibrary({ type: 'resource' });
    allLibraryResources.value = (response as any).data?.data?.results || [];
  } catch (err: any) {
    console.error(
      '[ResourceMetadataEditor] Error fetching library resources:',
      err
    );
    error.value = `Failed to load library resources: ${err.message}`;
  } finally {
    loadingLibraryResources.value = false;
  }
};

// Transform library resources for OrderedResourceSelect (exclude current resource, playlists; only Video and Webinar)
const ALLOWED_PLAYLIST_RESOURCE_TYPES = ['Video', 'Webinar'];
const transformedLibraryResources = computed(() => {
  const list = allLibraryResources.value
    .filter((item: any) => {
      if (item.vinciResourceId === props.resourceId) return false;
      if (item.resourceTypeId === 6) return false; // Exclude playlists
      // Filter to Video and Webinar only (by resourceTypeId or resourceType string)
      const isVideoOrWebinar =
        item.resourceType &&
        ALLOWED_PLAYLIST_RESOURCE_TYPES.includes(item.resourceType);
      return isVideoOrWebinar;
    })
    .map((item: any) => ({
      id: item.id,
      resourceId: item.vinciResourceId,
      title: item.title || ''
    }))
    .sort(
      (a: { resourceId: number }, b: { resourceId: number }) =>
        a.resourceId - b.resourceId
    );
  return list;
});

// Fetch published data from mod-library-v2
const fetchPublishedData = async () => {
  try {
    console.log(
      '[ResourceMetadataEditor] Fetching published data for resourceId:',
      props.resourceId
    );
    const response = await getResourceFromLibrary(props.resourceId);
    console.log('[ResourceMetadataEditor] Published data response:', response);
    publishedData.value = response || null;
    return publishedData.value;
  } catch (err: any) {
    console.error(
      '[ResourceMetadataEditor] Error fetching published data:',
      err
    );
    error.value = `Failed to load published data: ${err.message}`;
    return null;
  }
};

// Derive productType from resource data if not provided in props
const deriveProductTypeFromResourceData = (resourceData: any) => {
  if (props.productType) {
    // productType already provided in props, no need to derive
    return;
  }

  // Try to get resourceTypeId from resource data
  const resourceTypeId = resourceData?.resourceTypeId;

  if (resourceTypeId) {
    // Convert resourceTypeId to ResourceTypeName
    const resourceTypeName = getResourceTypeNameById(resourceTypeId);
    if (resourceTypeName) {
      // Convert ResourceTypeName to lowercase for productType
      const productTypeValue = resourceTypeName.toLowerCase() as
        | 'video'
        | 'webinar'
        | 'guide'
        | 'template'
        | 'blog'
        | 'playlist';
      derivedProductType.value = productTypeValue;
      console.log(
        '[ResourceMetadataEditor] Derived productType from resource data:',
        productTypeValue
      );
    }
  }
};

// Fetch draft data from mod-content (entire JSONB)
const fetchDraftData = async () => {
  try {
    console.log(
      '[ResourceMetadataEditor] Fetching draft data for resourceId:',
      props.resourceId
    );
    const result = await getResourceDraftMetadata(props.resourceId);
    console.log(
      '[ResourceMetadataEditor] Draft data (JSONB) response:',
      result
    );
    draftData.value = result;
    return draftData.value;
  } catch (err: any) {
    console.error('[ResourceMetadataEditor] Error fetching draft data:', err);
    // Don't set error for 404 - resource might not have draft data yet
    if (err.response?.status !== 404) {
      error.value = `Failed to load draft data: ${err.message}`;
    }
    return null;
  }
};

// Merge published and draft data into form
const mergeDataIntoForm = (published: any, draft: any) => {
  console.log('[ResourceMetadataEditor] Merging data into form...');
  console.log('[ResourceMetadataEditor] Published:', published);
  console.log('[ResourceMetadataEditor] Draft:', draft);

  // Start with empty form data
  const merged: any = {};

  // Initialize form data with defaults from FIELD_CONFIG
  Object.keys(FIELD_CONFIG).forEach(fieldPath => {
    const config = FIELD_CONFIG[fieldPath];
    // Only include fields that should be shown for this product type
    if (
      config.showFor === null ||
      (productType.value && config.showFor?.includes(productType.value))
    ) {
      // Get value from draft first, then published, then default
      const draftValue = draft ? getNestedValue(draft, fieldPath) : undefined;
      const publishedValue = published
        ? getNestedValue(published, fieldPath)
        : undefined;
      const value =
        draftValue !== undefined
          ? draftValue
          : publishedValue !== undefined
            ? publishedValue
            : config.defaultValue;

      // Set nested value
      setNestedValue(merged, fieldPath, value);
    }
  });

  // Ensure resourceType is set from productType (read-only field)
  const resourceTypeName = productTypeLabel.value as ResourceTypeName;
  merged.resourceType = resourceTypeName;

  // Calculate and set resourceTypeId (required field, but calculated)
  const resourceTypeId = getResourceTypeId(resourceTypeName);
  if (resourceTypeId !== undefined) {
    merged.resourceTypeId = resourceTypeId;
  }

  // Calculate and set activityTypeId if activityType is set (required field, but calculated)
  if (merged.activityType) {
    const activityTypeId = getActivityTypeId(
      resourceTypeName,
      merged.activityType
    );
    if (activityTypeId !== undefined) {
      merged.activityTypeId = activityTypeId;
    }
  }

  // Post-processing: Derive additionalInfo.description from descriptionHTML if missing
  // This ensures backwards compatibility when loading data
  // Use descriptionHTML as source of truth for comparison
  const descriptionHTML = getNestedValue(
    merged,
    'additionalInfo.descriptionHTML'
  );
  const description = getNestedValue(merged, 'additionalInfo.description');
  if (descriptionHTML && !description) {
    // Derive plain text description from HTML for backwards compatibility
    const plainText = stripHtml(descriptionHTML);
    setNestedValue(merged, 'additionalInfo.description', plainText);
  }

  // Post-processing: Derive targetLearners from resourceMaster.TargetLearners if missing
  // Use targetLearners (root level, array) as source of truth
  const targetLearnersArray = merged.targetLearners;
  const targetLearnersString = getNestedValue(
    merged,
    'resourceMaster.TargetLearners'
  );
  if (!targetLearnersArray && targetLearnersString) {
    // Convert comma-separated string to array
    const array =
      typeof targetLearnersString === 'string'
        ? targetLearnersString
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0)
        : [];
    merged.targetLearners = array;
  } else if (!targetLearnersArray) {
    // Initialize as empty array if neither exists
    merged.targetLearners = [];
  }

  // Post-processing: Set default thumbnailURL if not present
  // Default format: https://portal.vinciworks.com/client-assets/${vinciResourceId}.jpg
  if (!merged.thumbnailURL || merged.thumbnailURL === '') {
    const defaultThumbnailURL = `https://portal.vinciworks.com/client-assets/${props.resourceId}.jpg`;
    merged.thumbnailURL = defaultThumbnailURL;
  }

  console.log('[ResourceMetadataEditor] Merged form data:', merged);
  formData.value = merged;
};

// Merge only a specific section's data into the form (preserves unsaved changes in other sections)
const mergeSectionDataIntoForm = (
  sectionName: string,
  published: any,
  draft: any
) => {
  console.log(
    `[ResourceMetadataEditor] Merging ${sectionName} section data into form...`
  );

  // Get all fields for this section
  const sectionFields = getFieldsForSection(sectionName);

  // Update only the fields in this section
  sectionFields.forEach(fieldPath => {
    const config = FIELD_CONFIG[fieldPath];
    // Only include fields that should be shown for this product type
    if (
      config.showFor === null ||
      (productType.value && config.showFor?.includes(productType.value))
    ) {
      // Get value from draft first, then published, then default
      const draftValue = draft ? getNestedValue(draft, fieldPath) : undefined;
      const publishedValue = published
        ? getNestedValue(published, fieldPath)
        : undefined;
      const value =
        draftValue !== undefined
          ? draftValue
          : publishedValue !== undefined
            ? publishedValue
            : config.defaultValue;

      // Update only this field in the form
      setNestedValue(formData.value, fieldPath, value);
    }
  });

  // Special handling for sectionTitle: calculate and set suiteId and topicId
  if (sectionName === 'sectionTitle') {
    const suiteTitle = formData.value.suiteTitle;
    if (suiteTitle) {
      const selectedSuite = suites.value.find(
        suite => suite.title === suiteTitle
      );
      if (selectedSuite) {
        formData.value.suiteId = selectedSuite.internalId;
      }
    }

    const topicTitle = formData.value.topicTitle;
    if (topicTitle) {
      const selectedTopic = topics.value.find(
        topic => topic.title === topicTitle
      );
      if (selectedTopic) {
        formData.value.topicId = selectedTopic.internalId;
      }
    }
  }

  // Special handling for sectionType: calculate and set resourceTypeId and activityTypeId
  if (sectionName === 'sectionType') {
    const resourceTypeName = productTypeLabel.value as ResourceTypeName;
    formData.value.resourceType = resourceTypeName;

    const resourceTypeId = getResourceTypeId(resourceTypeName);
    if (resourceTypeId !== undefined) {
      formData.value.resourceTypeId = resourceTypeId;
    }

    if (formData.value.activityType) {
      const activityTypeId = getActivityTypeId(
        resourceTypeName,
        formData.value.activityType
      );
      if (activityTypeId !== undefined) {
        formData.value.activityTypeId = activityTypeId;
      }
    }
  }

  // Special handling for sectionAudience: derive resourceMaster.TargetLearners from targetLearners
  if (sectionName === 'sectionAudience') {
    const targetLearnersArray = formData.value.targetLearners || [];
    if (Array.isArray(targetLearnersArray) && targetLearnersArray.length > 0) {
      setNestedValue(
        formData.value,
        'resourceMaster.TargetLearners',
        targetLearnersArray.join(', ')
      );
    } else {
      setNestedValue(formData.value, 'resourceMaster.TargetLearners', '');
    }
  }

  console.log(
    `[ResourceMetadataEditor] Merged ${sectionName} section data into form`
  );
};

// Save section data
const saveSection = async (sectionName: string) => {
  savingSections[sectionName] = true;

  try {
    // Extract section data from form using FIELD_CONFIG
    const sectionData = extractFormData(sectionName);

    // For sectionAudience, derive resourceMaster.TargetLearners from targetLearners
    if (sectionName === 'sectionAudience') {
      // Convert targetLearners array to comma-separated string for resourceMaster.TargetLearners
      const targetLearnersArray = formData.value.targetLearners || [];
      if (
        Array.isArray(targetLearnersArray) &&
        targetLearnersArray.length > 0
      ) {
        sectionData['resourceMaster.TargetLearners'] =
          targetLearnersArray.join(', ');
      } else {
        sectionData['resourceMaster.TargetLearners'] = '';
      }
    }

    // For sectionTitle, also calculate and include suiteId and topicId
    if (sectionName === 'sectionTitle') {
      // Calculate suiteId from suiteTitle
      if (formData.value.suiteTitle) {
        const selectedSuite = suites.value.find(
          suite => suite.title === formData.value.suiteTitle
        );
        if (selectedSuite) {
          sectionData.suiteId = selectedSuite.internalId;
        }
      }

      // Calculate topicId from topicTitle
      if (formData.value.topicTitle) {
        const selectedTopic = topics.value.find(
          topic => topic.title === formData.value.topicTitle
        );
        if (selectedTopic) {
          sectionData.topicId = selectedTopic.internalId;
        }
      }
    }

    // For sectionType, also calculate and include resourceTypeId and activityTypeId
    if (sectionName === 'sectionType') {
      // Set resourceType from productType (read-only, but needs to be in saved data)
      const resourceTypeName = productTypeLabel.value as ResourceTypeName;
      sectionData.resourceType = resourceTypeName;

      // Calculate resourceTypeId from resourceType
      const resourceTypeId = getResourceTypeId(resourceTypeName);
      if (resourceTypeId !== undefined) {
        sectionData.resourceTypeId = resourceTypeId;
      }

      // Calculate activityTypeId from activityType
      if (formData.value.activityType) {
        const activityTypeId = getActivityTypeId(
          resourceTypeName,
          formData.value.activityType
        );
        if (activityTypeId !== undefined) {
          sectionData.activityTypeId = activityTypeId;
        }
      }
    }

    // Get section title from config
    const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

    await updateResourceDraftMetadataSection(
      props.resourceId,
      sectionName,
      sectionData,
      sectionTitle
    );

    // Refresh draft data to get updated metadata
    await fetchDraftData();

    // Only merge the saved section's data back into the form (to preserve unsaved changes in other sections)
    mergeSectionDataIntoForm(sectionName, publishedData.value, draftData.value);

    // Update section statuses after save
    await updateSectionStatus();

    console.log(
      `[ResourceMetadataEditor] Section ${sectionName} saved successfully`
    );
  } catch (err: any) {
    console.error(`[ResourceMetadataEditor] Error saving ${sectionName}:`, err);
    error.value = `Failed to save ${sectionName}: ${err.message}`;
  } finally {
    savingSections[sectionName] = false;
  }
};

// Get all field paths for a specific section from FIELD_CONFIG
const getFieldsForSection = (sectionName: string) => {
  return Object.keys(FIELD_CONFIG).filter(
    path => FIELD_CONFIG[path].section === sectionName
  );
};

// Whether a field should be shown for the current product type (uses FIELD_CONFIG.showFor)
const isFieldShown = (fieldPath: string) => {
  const config = FIELD_CONFIG[fieldPath];
  if (!config) return false;
  return (
    config.showFor === null ||
    (productType.value != null && config.showFor?.includes(productType.value))
  );
};

// Fields for a section that are shown for the current product type (used for compare/save so hidden fields like contentType for playlist are ignored)
const getFieldsForSectionShown = (sectionName: string) => {
  return getFieldsForSection(sectionName).filter(path => isFieldShown(path));
};

// Extract current form data for a specific section (only fields shown for current product type)
const extractFormData = (sectionName: string) => {
  const sectionFields = getFieldsForSectionShown(sectionName);
  const data: Record<string, any> = {};

  sectionFields.forEach(path => {
    const value = getNestedValue(formData.value, path);
    // Use full path as key to preserve nesting structure (e.g., "additionalInfo.Industry")
    data[path] = value;
  });

  return data;
};

// Check if a section has unsaved changes (form data differs from saved draft)
const hasSectionChanges = (sectionName: string) => {
  const draft = draftData.value;
  if (!draft) {
    // No saved draft - check if form has any data
    const formDataForSection = extractFormData(sectionName);
    const hasData = Object.values(formDataForSection).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null && value !== undefined;
    });
    if (hasData) {
      console.log(
        `[ResourceMetadataEditor] ${sectionName}: No draft, but form has data`
      );
    }
    return hasData;
  }

  // Compare current form data against saved draft data (only fields shown for current product type)
  const currentFormData = extractFormData(sectionName);
  const savedDraftData: Record<string, any> = {};

  // Extract saved draft data for this section using the same path-based structure (shown fields only)
  const sectionFields = getFieldsForSectionShown(sectionName);
  sectionFields.forEach(path => {
    const value = getNestedValue(draft, path);
    savedDraftData[path] = value;
  });

  // Compare using areValuesEqual for each field (since we're using paths as keys)
  const hasChanges = sectionFields.some(path => {
    const formValue = currentFormData[path];
    const draftValue = savedDraftData[path];
    const areEqual = areValuesEqual(formValue, draftValue);
    if (!areEqual) {
      console.log(
        `[ResourceMetadataEditor] ${sectionName}: Field "${path}" differs:`,
        {
          formValue,
          draftValue,
          formType: typeof formValue,
          draftType: typeof draftValue,
          areEqual: areValuesEqual(formValue, draftValue)
        }
      );
    }
    return !areEqual;
  });

  // Special debug logging for sectionInfo
  if (sectionName === 'sectionInfo') {
    console.log(`[ResourceMetadataEditor] sectionInfo comparison:`, {
      currentFormData,
      savedDraftData,
      hasChanges
    });
  }

  if (hasChanges) {
    console.log(
      `[ResourceMetadataEditor] ${sectionName}: Has unsaved changes`,
      {
        currentFormData,
        savedDraftData
      }
    );
  }

  return hasChanges;
};

// Check if a section has all required fields filled
const hasRequiredFields = (sectionName: string) => {
  const sectionFields = getFieldsForSection(sectionName);

  const result = sectionFields.every(path => {
    const config = FIELD_CONFIG[path];
    if (!config.required) {
      return true; // Not required, so it's valid
    }

    // Check if field should be shown for this product type
    if (
      config.showFor !== null &&
      (!productType.value || !config.showFor.includes(productType.value))
    ) {
      return true; // Field not shown for this product type, so it's valid
    }

    // Check if field is required for this product type
    if (
      config.requiredFor !== null &&
      (!productType.value || !config.requiredFor.includes(productType.value))
    ) {
      return true; // Field not required for this product type, so it's valid
    }

    const value = getNestedValue(formData.value, path);

    // Check for non-empty values
    if (Array.isArray(value)) {
      const isValid = value.length > 0;
      if (!isValid && sectionName === 'sectionAudience') {
        console.log(
          `[ResourceMetadataEditor] Required field "${path}" is empty array:`,
          value
        );
      }
      return isValid;
    }
    if (typeof value === 'string') {
      const isValid = value.trim() !== '';
      if (!isValid && sectionName === 'sectionAudience') {
        console.log(
          `[ResourceMetadataEditor] Required field "${path}" is empty string:`,
          value
        );
      }
      return isValid;
    }
    if (typeof value === 'number') {
      return value !== null && value !== undefined;
    }
    const isValid = value !== null && value !== undefined;
    if (!isValid && sectionName === 'sectionAudience') {
      console.log(
        `[ResourceMetadataEditor] Required field "${path}" is null/undefined:`,
        value
      );
    }
    return isValid;
  });

  if (sectionName === 'sectionAudience') {
    console.log(
      `[ResourceMetadataEditor] hasRequiredFields('${sectionName}'):`,
      result
    );
    console.log(`[ResourceMetadataEditor] Form data for sectionAudience:`, {
      'additionalInfo.Industry': getNestedValue(
        formData.value,
        'additionalInfo.Industry'
      ),
      'additionalInfo.industryId': getNestedValue(
        formData.value,
        'additionalInfo.industryId'
      ),
      'additionalInfo.Jurisdiction': getNestedValue(
        formData.value,
        'additionalInfo.Jurisdiction'
      ),
      'additionalInfo.jurisdictionId': getNestedValue(
        formData.value,
        'additionalInfo.jurisdictionId'
      ),
      targetLearners: formData.value.targetLearners,
      'resourceMaster.TargetLearners': getNestedValue(
        formData.value,
        'resourceMaster.TargetLearners'
      )
    });
  }

  return result;
};

// Enhanced section validation - must have changes AND required fields
const canSaveSection = (sectionName: string) => {
  return hasSectionChanges(sectionName) && hasRequiredFields(sectionName);
};

// Update section status based on data
const updateSectionStatus = async () => {
  const draft = draftData.value;
  const published = publishedData.value;

  console.log('[ResourceMetadataEditor] Updating section statuses...', {
    hasPublished: !!published && Object.keys(published).length > 0,
    hasDraft: !!draft && Object.keys(draft).length > 0
  });

  // Calculate status for each section
  Object.keys(SECTION_CONFIG).forEach(sectionName => {
    // First check if there are unsaved changes (form data differs from saved draft data)
    const hasUnsavedChanges = hasSectionChanges(sectionName);

    if (hasUnsavedChanges) {
      sectionStatus[sectionName] = SECTION_STATUSES.UNSAVED_CHANGES;
      console.log(
        `[ResourceMetadataEditor] ${sectionName}: UNSAVED_CHANGES (form differs from draft)`
      );
    } else {
      // No unsaved changes, use calculateResourceSectionStatus
      // This compares published vs draft (saved JSONB)
      const status = calculateResourceSectionStatus(
        sectionName,
        published,
        draft,
        FIELD_CONFIG,
        productType.value
      );
      sectionStatus[sectionName] = status;
      console.log(
        `[ResourceMetadataEditor] ${sectionName}: ${status} (published vs draft)`
      );
    }
  });

  console.log(
    '[ResourceMetadataEditor] Section statuses updated:',
    sectionStatus
  );
};

// Get status display text and color
const getStatusDisplay = (status: string) => {
  switch (status) {
    case SECTION_STATUSES.NOT_STARTED:
      return { text: 'Not Started', color: 'text-gray-400' };
    case SECTION_STATUSES.READY_TO_PUBLISH:
      return { text: 'Ready To Publish', color: 'text-blue-600' };
    case SECTION_STATUSES.READY_TO_UPDATE:
      return { text: 'Ready To Update', color: 'text-orange-600' };
    case SECTION_STATUSES.PUBLISHED:
      return { text: 'Published', color: 'text-green-700' };
    case SECTION_STATUSES.UNSAVED_CHANGES:
      return { text: 'Unsaved Changes', color: 'text-red-600' };
    default:
      return { text: 'Unknown', color: 'text-gray-500' };
  }
};

// Get badge class for status
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case SECTION_STATUSES.NOT_STARTED:
      return 'bg-gray-200 text-gray-800';
    case SECTION_STATUSES.READY_TO_PUBLISH:
      return 'bg-blue-100 text-blue-800';
    case SECTION_STATUSES.READY_TO_UPDATE:
      return 'bg-orange-100 text-orange-800';
    case SECTION_STATUSES.PUBLISHED:
      return 'bg-green-100 text-green-800';
    case SECTION_STATUSES.UNSAVED_CHANGES:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-200 text-gray-800';
  }
};

// Determine if publish/update button should be enabled
const canPublish = computed(() => {
  const hasPublishedData =
    publishedData.value && Object.keys(publishedData.value).length > 0;

  const formSections = Object.keys(sectionStatus);

  if (!hasPublishedData) {
    // No published data exists - enable only when ALL form sections are "Ready To Publish"
    return formSections.every(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_PUBLISH
    );
  } else {
    // Has published data - enable when AT LEAST ONE form section is "Ready To Update"
    return formSections.some(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
    );
  }
});

// Check if reset buttons should be visible (only when there's published data)
const showResetButtons = computed(() => {
  return publishedData.value && Object.keys(publishedData.value).length > 0;
});

// Check if global reset form button should be visible
const showGlobalResetButton = computed(() => {
  return (
    showResetButtons.value &&
    Object.keys(sectionStatus).some(
      section => sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
    )
  );
});

// Check if a specific section's reset button should be visible
const canResetSection = (sectionName: string) => {
  return (
    showResetButtons.value &&
    sectionStatus[sectionName] === SECTION_STATUSES.READY_TO_UPDATE
  );
};

// Extract published data for a specific section (for reset functionality) - only shown fields for current product type
const extractPublishedSectionData = (sectionName: string) => {
  if (!publishedData.value) return {};

  const sectionFields = getFieldsForSectionShown(sectionName);
  const data: Record<string, any> = {};

  sectionFields.forEach(path => {
    // Get raw value from published data without default fallback
    const value = path
      .split('.')
      .reduce((acc, part) => acc && acc[part], publishedData.value);
    // Use full path as key to preserve nesting structure
    data[path] = value;
  });

  return data;
};

// Update form with published data for a specific section - only shown fields for current product type
const updateFormWithPublishedData = (
  sectionName: string,
  dataToRestore: Record<string, any>
) => {
  const sectionFields = getFieldsForSectionShown(sectionName);

  sectionFields.forEach(path => {
    let value = dataToRestore[path];

    // Use the default value from config if the restored value is undefined
    if (value === undefined) {
      value = FIELD_CONFIG[path].defaultValue;
    }

    setNestedValue(formData.value, path, value);
  });
};

// Reset a specific section to published values
const resetSection = async (sectionName: string) => {
  try {
    // Extract published data for this section
    const publishedSectionData = extractPublishedSectionData(sectionName);

    // Update the form with published values
    updateFormWithPublishedData(sectionName, publishedSectionData);

    // For sectionTitle, also calculate and include suiteId and topicId
    if (sectionName === 'sectionTitle') {
      // Calculate suiteId from suiteTitle (from published data or form)
      const suiteTitle =
        publishedSectionData.suiteTitle || formData.value.suiteTitle;
      if (suiteTitle) {
        const selectedSuite = suites.value.find(
          suite => suite.title === suiteTitle
        );
        if (selectedSuite) {
          publishedSectionData.suiteId = selectedSuite.internalId;
        }
      }

      // Calculate topicId from topicTitle (from published data or form)
      const topicTitle =
        publishedSectionData.topicTitle || formData.value.topicTitle;
      if (topicTitle) {
        const selectedTopic = topics.value.find(
          topic => topic.title === topicTitle
        );
        if (selectedTopic) {
          publishedSectionData.topicId = selectedTopic.internalId;
        }
      }
    }

    // For sectionType, also calculate and include resourceTypeId and activityTypeId
    if (sectionName === 'sectionType') {
      // Set resourceType from productType (read-only, but needs to be in saved data)
      const resourceTypeName = productTypeLabel.value as ResourceTypeName;
      publishedSectionData.resourceType = resourceTypeName;

      // Calculate resourceTypeId from resourceType
      const resourceTypeId = getResourceTypeId(resourceTypeName);
      if (resourceTypeId !== undefined) {
        publishedSectionData.resourceTypeId = resourceTypeId;
      }

      // Calculate activityTypeId from activityType (from published data or form)
      const activityType =
        publishedSectionData.activityType || formData.value.activityType;
      if (activityType) {
        const activityTypeId = getActivityTypeId(
          resourceTypeName,
          activityType
        );
        if (activityTypeId !== undefined) {
          publishedSectionData.activityTypeId = activityTypeId;
        }
      }
    }

    // Get section title from config
    const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

    // Update draft data with the published values
    // This effectively "resets" the section by making draft match published
    await updateResourceDraftMetadataSection(
      props.resourceId,
      sectionName,
      publishedSectionData,
      sectionTitle
    );

    // Refresh draft data and update statuses
    await fetchDraftData();
    mergeDataIntoForm(publishedData.value, draftData.value);
    await updateSectionStatus();

    console.log(
      `[ResourceMetadataEditor] Section ${sectionName} reset successfully`
    );
  } catch (err: any) {
    console.error(
      `[ResourceMetadataEditor] Error resetting ${sectionName}:`,
      err
    );
    throw err;
  }
};

// Publish/Update handlers
const handlePublishUpdate = () => {
  showPublishModal.value = true;
};

const closePublishModal = () => {
  if (!isPublishing.value) {
    showPublishModal.value = false;
  }
};

const confirmPublish = async () => {
  try {
    isPublishing.value = true;

    // Calculate suiteId and topicId
    let suiteId: number | null = null;
    if (formData.value.suiteTitle) {
      const selectedSuite = suites.value.find(
        suite => suite.title === formData.value.suiteTitle
      );
      if (selectedSuite) {
        suiteId = selectedSuite.internalId;
      }
    }

    let topicId: number | null = null;
    if (formData.value.topicTitle) {
      const selectedTopic = topics.value.find(
        topic => topic.title === formData.value.topicTitle
      );
      if (selectedTopic) {
        topicId = selectedTopic.internalId;
      }
    }

    // Calculate resourceTypeId and activityTypeId
    const resourceTypeName = productTypeLabel.value as ResourceTypeName;
    const resourceTypeId = getResourceTypeId(resourceTypeName);
    let activityTypeId: number | null = null;
    if (formData.value.activityType) {
      const calculatedActivityTypeId = getActivityTypeId(
        resourceTypeName,
        formData.value.activityType
      );
      if (calculatedActivityTypeId !== undefined) {
        activityTypeId = calculatedActivityTypeId;
      }
    }

    // Convert targetLearners array to comma-separated string for resourceMaster.TargetLearners
    const targetLearnersArray = formData.value.targetLearners || [];
    const targetLearnersString =
      Array.isArray(targetLearnersArray) && targetLearnersArray.length > 0
        ? targetLearnersArray.join(', ')
        : '';

    // Build the payload structure matching the examples
    const payloadToNormalize: any = {
      vinciResourceId: props.resourceId,
      type: 'resource',
      title: formData.value.title || '',
      suiteId: suiteId,
      topicId: topicId,
      suiteTitle: formData.value.suiteTitle || '',
      topicTitle: formData.value.topicTitle || '',
      flagship: String(formData.value.flagship || 'false'),
      level: formData.value.level || 'All Levels',
      activityType: formData.value.activityType || '',
      activityTypeId: activityTypeId,
      resourceType: resourceTypeName,
      resourceTypeId: resourceTypeId || null,
      contentType: formData.value.contentType || null,
      retired:
        typeof formData.value.retired === 'boolean'
          ? formData.value.retired
          : false,
      targetLearners: targetLearnersArray,
      resourceUrl: formData.value.resourceUrl || null,
      thumbnailURL: formData.value.thumbnailURL || '',
      objectivesHTML: formData.value.objectivesHTML || null,
      duration: formData.value.duration || null, // For video/webinar
      additionalInfo: {
        Industry:
          getNestedValue(formData.value, 'additionalInfo.Industry') || '',
        industryId:
          getNestedValue(formData.value, 'additionalInfo.industryId') || '',
        description:
          getNestedValue(formData.value, 'additionalInfo.description') || '',
        descriptionHTML:
          getNestedValue(formData.value, 'additionalInfo.descriptionHTML') ||
          '',
        Jurisdiction:
          getNestedValue(formData.value, 'additionalInfo.Jurisdiction') || [],
        jurisdictionId:
          getNestedValue(formData.value, 'additionalInfo.jurisdictionId') || [],
        relatedTo:
          getNestedValue(formData.value, 'additionalInfo.relatedTo') || [],
        legislation:
          getNestedValue(formData.value, 'additionalInfo.legislation') || null,
        triggerWarning:
          getNestedValue(formData.value, 'additionalInfo.triggerWarning') ||
          false
      },
      resourceMaster: {
        TargetLearners: targetLearnersString
      }
    };

    // Add video-specific fields if product type is video
    if (productType.value === 'video') {
      payloadToNormalize.videoTranscriptURL =
        formData.value.videoTranscriptURL || null;
      payloadToNormalize.videoCaptionUrl =
        formData.value.videoCaptionUrl || null;
    }

    // Add playlist-specific field (ordered resource IDs at root level)
    if (productType.value === 'playlist') {
      payloadToNormalize.hasResourceIds = Array.isArray(
        formData.value.hasResourceIds
      )
        ? formData.value.hasResourceIds
        : [];
    }

    console.log(
      '[ResourceMetadataEditor] Publishing/Updating with payload:',
      payloadToNormalize
    );

    // Call publish or update API
    if (!publishedData.value || Object.keys(publishedData.value).length === 0) {
      await publishResourceToLibrary(payloadToNormalize);
    } else {
      await updateResourceInLibrary(props.resourceId, payloadToNormalize);
    }

    // Stop loading before closing so the modal can close immediately
    isPublishing.value = false;
    closePublishModal();

    // Refresh published data
    await fetchPublishedData();

    // Sync draft to published so sections flip to Published and save buttons disable
    const formSections = Object.keys(SECTION_CONFIG);
    for (const sectionName of formSections) {
      const publishedSectionData = extractPublishedSectionData(sectionName);

      // Get section title from config
      const sectionTitle = SECTION_CONFIG[sectionName]?.title || sectionName;

      // For sectionTitle, also calculate and include suiteId and topicId
      if (sectionName === 'sectionTitle') {
        // Calculate suiteId from suiteTitle (from published data or form)
        const suiteTitle =
          publishedSectionData['suiteTitle'] || formData.value.suiteTitle;
        if (suiteTitle) {
          const selectedSuite = suites.value.find(
            suite => suite.title === suiteTitle
          );
          if (selectedSuite) {
            publishedSectionData['suiteId'] = selectedSuite.internalId;
          }
        }

        // Calculate topicId from topicTitle (from published data or form)
        const topicTitle =
          publishedSectionData['topicTitle'] || formData.value.topicTitle;
        if (topicTitle) {
          const selectedTopic = topics.value.find(
            topic => topic.title === topicTitle
          );
          if (selectedTopic) {
            publishedSectionData['topicId'] = selectedTopic.internalId;
          }
        }
      }

      // For sectionType, also calculate and include resourceTypeId and activityTypeId
      if (sectionName === 'sectionType') {
        const resourceTypeName = productTypeLabel.value as ResourceTypeName;
        publishedSectionData['resourceType'] = resourceTypeName;

        // Calculate resourceTypeId from resourceType
        const calculatedResourceTypeId = getResourceTypeId(resourceTypeName);
        if (calculatedResourceTypeId !== undefined) {
          publishedSectionData['resourceTypeId'] = calculatedResourceTypeId;
        }

        // Calculate activityTypeId from activityType
        const activityType =
          publishedSectionData['activityType'] || formData.value.activityType;
        if (activityType) {
          const calculatedActivityTypeId = getActivityTypeId(
            resourceTypeName,
            activityType
          );
          if (calculatedActivityTypeId !== undefined) {
            publishedSectionData['activityTypeId'] = calculatedActivityTypeId;
          }
        }
      }

      // For sectionAudience, derive resourceMaster.TargetLearners from targetLearners
      if (sectionName === 'sectionAudience') {
        const targetLearnersArray =
          publishedSectionData['targetLearners'] ||
          formData.value.targetLearners ||
          [];
        if (
          Array.isArray(targetLearnersArray) &&
          targetLearnersArray.length > 0
        ) {
          publishedSectionData['resourceMaster.TargetLearners'] =
            targetLearnersArray.join(', ');
        } else {
          publishedSectionData['resourceMaster.TargetLearners'] = '';
        }
      }

      await updateResourceDraftMetadataSection(
        props.resourceId,
        sectionName,
        publishedSectionData,
        sectionTitle
      );
    }

    await fetchDraftData();
    await updateSectionStatus();
    error.value = '';
  } catch (err: any) {
    // Sanitize error message
    let message = 'Failed to publish/update resource';
    // @ts-ignore
    if (err?.response?.data?.message) {
      // @ts-ignore
      message = `Publish/Update failed: ${err.response.data.message}`;
      // @ts-ignore
    } else if (err?.response?.data?.error) {
      // @ts-ignore
      message = `Publish/Update failed: ${err.response.data.error}`;
    } else if (err instanceof Error && err.message) {
      message = `Publish/Update failed: ${err.message}`;
    }
    error.value = message;
    // Keep modal open so user can see the error
  } finally {
    // Ensure loading flag is cleared if we errored before clearing above
    isPublishing.value = false;
  }
};

// Delete handlers
const openDeleteModal = () => {
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

const confirmDelete = async () => {
  try {
    isDeleting.value = true;
    await deleteResourceFromLibrary(props.resourceId);
    // Refresh published data to reflect deletion
    await fetchPublishedData();
    await updateSectionStatus();
  } catch (err) {
    console.error('Failed to delete resource:', err);
    error.value = 'Failed to delete resource';
  } finally {
    isDeleting.value = false;
    closeDeleteModal();
  }
};

// Reset handlers
const openResetModal = (target: string) => {
  resetTarget.value = target;
  if (target === 'all') {
    resetModalTitle.value = 'Reset All Sections?';
  } else {
    resetModalTitle.value = `Reset ${SECTION_CONFIG[target].title} Section?`;
  }
  showResetModal.value = true;
};

const closeResetModal = () => {
  showResetModal.value = false;
  resetTarget.value = '';
};

const confirmReset = async () => {
  try {
    if (resetTarget.value === 'all') {
      // Reset all sections that have changes
      const sectionsToReset = Object.keys(sectionStatus).filter(
        section => sectionStatus[section] === SECTION_STATUSES.READY_TO_UPDATE
      );

      // Reset each section sequentially
      for (const sectionName of sectionsToReset) {
        await resetSection(sectionName);
      }
    } else {
      await resetSection(resetTarget.value);
    }
    closeResetModal();
  } catch (err: any) {
    console.error('[ResourceMetadataEditor] Error resetting:', err);
    error.value = `Failed to reset: ${err.message}`;
  }
};

// Watch for changes in suite and clear topic if suite changes
watch(
  () => formData.value.suiteTitle,
  (newVal, oldVal) => {
    if (newVal !== oldVal && oldVal) {
      formData.value.topicTitle = '';
    }
  }
);

// Watch for changes in activityType and calculate activityTypeId
watch(
  () => formData.value.activityType,
  newVal => {
    if (newVal) {
      const resourceTypeName = productTypeLabel.value as ResourceTypeName;
      const activityTypeId = getActivityTypeId(resourceTypeName, newVal);
      if (activityTypeId !== undefined) {
        formData.value.activityTypeId = activityTypeId;
      }
    } else {
      // Clear activityTypeId if activityType is cleared
      formData.value.activityTypeId = null;
    }
  }
);

// Watch for changes in targetLearners and derive resourceMaster.TargetLearners
watch(
  () => formData.value.targetLearners,
  newVal => {
    // Ensure resourceMaster object exists
    if (!formData.value.resourceMaster) {
      formData.value.resourceMaster = {};
    }
    // Convert array to comma-separated string
    if (Array.isArray(newVal) && newVal.length > 0) {
      formData.value.resourceMaster.TargetLearners = newVal.join(', ');
    } else {
      formData.value.resourceMaster.TargetLearners = '';
    }
  },
  { deep: true }
);

// Handle industry change - update both industryId and Industry name
const handleIndustryChange = (selection: { id: string; name: string }) => {
  console.log(
    '[ResourceMetadataEditor] handleIndustryChange called:',
    selection
  );
  // Ensure additionalInfo object exists
  if (!formData.value.additionalInfo) {
    formData.value.additionalInfo = {};
  }
  // Update both the ID and name in formData
  setNestedValue(formData.value, 'additionalInfo.industryId', selection.id);
  setNestedValue(formData.value, 'additionalInfo.Industry', selection.name);
  console.log('[ResourceMetadataEditor] After handleIndustryChange:', {
    industryId: formData.value.additionalInfo?.industryId,
    Industry: formData.value.additionalInfo?.Industry
  });
};

// Load data on component mount
onMounted(async () => {
  loading.value = true;
  error.value = '';

  try {
    console.log('[ResourceMetadataEditor] Component mounted, loading data...');
    console.log('[ResourceMetadataEditor] Props:', {
      resourceId: props.resourceId,
      firmCode: props.firmCode,
      productType: props.productType
    });
    console.log(
      '[ResourceMetadataEditor] Computed productType:',
      productType.value
    );

    // Fetch dropdown data first so it's available when merging form data
    await fetchDropdownData();

    // Fetch library courses (for relatedTo) and resources (for playlist hasResourceIds)
    await Promise.all([fetchAllLibraryCourses(), fetchAllLibraryResources()]);

    const [published, draft] = await Promise.all([
      fetchPublishedData(),
      fetchDraftData()
    ]);

    // Derive productType from resource data if not provided in props
    // Try published data first, then draft data
    if (!props.productType) {
      deriveProductTypeFromResourceData(published);
      if (!derivedProductType.value && draft) {
        deriveProductTypeFromResourceData(draft);
      }
    }

    // If productType is still not available, show error
    if (!productType.value) {
      error.value =
        'Unable to determine resource type. Please provide productType query parameter or ensure resource has resourceTypeId.';
      loading.value = false;
      return;
    }

    mergeDataIntoForm(published, draft);

    // Update section statuses after data is loaded
    await updateSectionStatus();

    console.log('[ResourceMetadataEditor] Data loading complete!');
    console.log(
      '[ResourceMetadataEditor] Published data:',
      publishedData.value
    );
    console.log('[ResourceMetadataEditor] Draft data:', draftData.value);
    console.log('[ResourceMetadataEditor] Form data:', formData.value);
  } catch (err: any) {
    console.error('[ResourceMetadataEditor] Error loading data:', err);
    error.value = `Failed to load data: ${err.message}`;
  } finally {
    loading.value = false;
  }
});

// Watch for form data changes to update statuses in real-time
watch(
  formData,
  async () => {
    await updateSectionStatus();
  },
  { deep: true }
);
</script>

<style scoped>
.required-asterisk {
  font-weight: bold;
  margin-left: 2px;
}

/* Reset confirmation button */
.reset-confirm-btn {
  background-color: #dc2626 !important; /* bg-red-600 */
  color: white !important;
  padding: 0.5rem 1rem !important;
  border-radius: 0.25rem !important;
  font-weight: 500 !important;
  transition: all 0.2s !important;
}

.reset-confirm-btn:hover {
  background-color: #b91c1c !important; /* bg-red-700 */
}

.reset-confirm-btn:active {
  background-color: #991b1b !important; /* bg-red-800 */
}

/* Modal overlay */
.modal-overlay {
  backdrop-filter: blur(2px);
}

.modal-content {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
